<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  isCollapsed: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    v-tooltip.right="isCollapsed ? label : ''"
    class="group relative flex h-9 min-w-0 items-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-n-brand/40"
    role="button"
    draggable="false"
    :to="to"
    :title="!isCollapsed ? label : ''"
    :class="[
      isCollapsed ? 'justify-center px-0' : 'gap-2.5 px-2.5',
      {
        'bg-n-brand/10 text-n-brand font-medium before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-n-brand':
          (isActive && !hasActiveChild) || (isCollapsed && hasActiveChild),
        'text-n-slate-12 font-medium': hasActiveChild && !isCollapsed,
        'text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12':
          !isActive && !hasActiveChild,
      },
    ]"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex shrink-0 items-center">
      <Icon :icon="icon" class="size-[18px]" />
      <span
        v-if="showBadge"
        class="absolute -top-px ltr:-right-px rtl:-left-px size-2 rounded-full border border-n-solid-2 bg-n-brand"
      />
    </div>
    <div
      v-if="!isCollapsed"
      class="flex min-w-0 flex-grow items-center gap-1.5"
    >
      <span class="truncate text-[13.5px] leading-5 tracking-tight">
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="ms-auto shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4 tabular-nums"
        :class="{
          'bg-n-brand/15 text-n-brand': isActive,
          'bg-n-slate-3 text-n-slate-11': !isActive,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable && !isCollapsed"
      class="ms-auto size-3.5 shrink-0 text-n-slate-10 transition-transform duration-150"
      :class="[isExpanded ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right']"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
